import { inject, Injectable } from '@angular/core';
import { Quote, QuoteStatus } from '@app/core/models/quote.interface';
import { JobItem } from '@app/core/models/job.interface';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { parametersFromPath } from '@app/core/interceptors/mock-api.interceptor';
import { CustomerMocksService } from '@app/core/services/mocks/customer-mocks.service';
import { VehicleMocksService } from '@app/core/services/mocks/vehicle-mocks.service';
import { faker } from '@faker-js/faker';
import { ApiResponse } from '@app/core/adaptors/dto';

@Injectable({ providedIn: 'root' })
export class QuoteMocksService {
  private readonly STORAGE_KEY = 'mock_quotes';
  private readonly ITEMS_STORAGE_KEY = 'mock_quote_items';
  private customerMocksService = inject(CustomerMocksService);
  private vehicleMocksService = inject(VehicleMocksService);

  private quotes: Quote[] = [];
  private quoteItems = new Map<number, JobItem[]>();
  private nextId = 1;
  private nextItemId = 1;

  constructor() {
    this.loadFromStorage();
    this.loadItemsFromStorage();
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    // Check if this is a quote items request (URL contains /items)
    if (req.url.includes('/items')) {
      return this.handleQuoteItemRequest(req);
    }

    // Check for convert to job: POST .../quotes/:id/convert
    if (req.method === 'POST' && req.url.includes('/convert')) {
      const { resourceId } = parametersFromPath(req, 'quotes');
      if (!resourceId) throw new Error('Quote ID is required for conversion');
      return this.convertToJob(resourceId);
    }

    // Check for status update: PATCH .../quotes/:id/status
    if (req.method === 'PATCH' && req.url.includes('/status')) {
      const { resourceId } = parametersFromPath(req, 'quotes');
      if (!resourceId) throw new Error('Quote ID is required for status update');
      return this.updateStatus(resourceId, req.body);
    }

    const { resourceId } = parametersFromPath(req, 'quotes');

    switch (req.method) {
      case 'GET':
        if (resourceId) {
          return this.getQuote(resourceId);
        }
        return this.getQuotes(req);
      case 'POST':
        return this.createQuote(req.body);
      case 'PUT':
        if (resourceId) {
          return this.updateQuote(resourceId, req.body);
        }
        throw new Error('Quote ID is required for update');
      case 'DELETE':
        if (resourceId) {
          return this.deleteQuote(resourceId);
        }
        throw new Error('Quote ID is required for delete');
      default:
        throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  private getQuotes(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (this.quotes.length === 0) {
      this.generateMockQuotes();
      this.saveToStorage();
    }

    const url = new URL(req.url);
    const params = url.searchParams;
    const page = parseInt(params.get('page') || '1', 10);
    const limit = parseInt(params.get('limit') || '10', 10);
    const query = params.get('query');
    const customerId = params.get('customerId');
    const vehicleRego = params.get('vehicleRego');
    const status = params.get('status');

    let filtered = [...this.quotes];

    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter(
        (quote) =>
          quote.quoteNumber.toLowerCase().includes(q) ||
          quote.customerName.toLowerCase().includes(q) ||
          quote.vehicleRego.toLowerCase().includes(q)
      );
    }
    if (customerId) {
      filtered = filtered.filter((q) => q.customerId === parseInt(customerId, 10));
    }
    if (vehicleRego) {
      filtered = filtered.filter((q) => q.vehicleRego === vehicleRego);
    }
    if (status) {
      filtered = filtered.filter((q) => q.status === status);
    }

    const data = filtered.slice((page - 1) * limit, page * limit);

    const res: ApiResponse<Quote> = {
      data,
      total: filtered.length,
      page,
      limit,
      totalPages: Math.ceil(filtered.length / limit),
    };
    return this.mapToResponse(res, 200);
  }

  private getQuote(id: number): Observable<HttpEvent<any>> {
    const quote = this.quotes.find((q) => q.id === id);
    if (!quote) {
      return this.mapToResponse({ error: 'Quote not found' }, 404);
    }
    return this.mapToResponse(quote, 200);
  }

  private createQuote(body: any): Observable<HttpEvent<any>> {
    const customer = this.customerMocksService
      .getMockCustomers()
      .find((c) => c.id === body.customerId);

    const newQuote: Quote = {
      id: this.nextId++,
      tenantId: 1,
      vehicleId: body.vehicleId,
      quoteNumber: `QT-01-${this.nextId - 1}`,
      customerId: body.customerId,
      customerName: customer?.name || 'Unknown Customer',
      customerEmail: 'k@y.com',
      vehicleRego: body.vehicleRego,
      jobId: body.jobId || null,
      status: 'PENDING',
      expiryDate: body.expiryDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      notes: body.notes || null,
      createdBy: null,
      updatedBy: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.quotes.unshift(newQuote);
    this.saveToStorage();
    return this.mapToResponse(newQuote, 201);
  }

  private updateQuote(id: number, body: any): Observable<HttpEvent<any>> {
    const quote = this.quotes.find((q) => q.id === id);
    if (!quote) {
      return this.mapToResponse({ error: 'Quote not found' }, 404);
    }

    if (body.customerId != null) quote.customerId = body.customerId;
    if (body.vehicleRego != null) quote.vehicleRego = body.vehicleRego;
    if (body.notes !== undefined) quote.notes = body.notes;
    if (body.expiryDate !== undefined) quote.expiryDate = body.expiryDate;

    if (body.customerId != null) {
      const customer = this.customerMocksService
        .getMockCustomers()
        .find((c) => c.id === body.customerId);
      if (customer) quote.customerName = customer.name;
    }

    quote.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return this.mapToResponse(quote, 200);
  }

  private deleteQuote(id: number): Observable<HttpEvent<any>> {
    const index = this.quotes.findIndex((q) => q.id === id);
    if (index === -1) {
      return this.mapToResponse({ error: 'Quote not found' }, 404);
    }
    this.quotes.splice(index, 1);
    this.saveToStorage();
    return this.mapToResponse(null, 200);
  }

  private updateStatus(id: number, body: { status: QuoteStatus }): Observable<HttpEvent<any>> {
    const quote = this.quotes.find((q) => q.id === id);
    if (!quote) {
      return this.mapToResponse({ error: 'Quote not found' }, 404);
    }

    const allowedTransitions: Record<QuoteStatus, QuoteStatus[]> = {
      PENDING: ['ACCEPTED', 'DECLINED', 'EXPIRED'],
      ACCEPTED: ['IN_PROGRESS', 'EXPIRED', 'JOB_CREATED'],
      DECLINED: [],
      EXPIRED: [],
      IN_PROGRESS: [],
      JOB_CREATED: [],
    };

    const allowed = allowedTransitions[quote.status] || [];
    if (!allowed.includes(body.status)) {
      return this.mapToResponse(
        { error: `Cannot transition from ${quote.status} to ${body.status}` },
        400
      );
    }

    quote.status = body.status;
    quote.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return this.mapToResponse(quote, 200);
  }

  private convertToJob(id: number): Observable<HttpEvent<any>> {
    const quote = this.quotes.find((q) => q.id === id);
    if (!quote) {
      return this.mapToResponse({ error: 'Quote not found' }, 404);
    }
    if (quote.status !== 'ACCEPTED') {
      return this.mapToResponse({ error: `Cannot convert quote with status ${quote.status}` }, 400);
    }

    quote.status = 'JOB_CREATED';
    quote.jobId = Math.floor(Math.random() * 1000) + 100;
    quote.updatedAt = new Date().toISOString();
    this.saveToStorage();
    return this.mapToResponse(quote, 200);
  }

  // --- Quote Items ---

  private handleQuoteItemRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const { resourceId: quoteId } = parametersFromPath(req, 'quotes');
    const { resourceId: itemId } = parametersFromPath(req, 'items');

    if (!quoteId) {
      throw new Error('Quote ID is required for quote items');
    }

    switch (req.method) {
      case 'GET':
        return this.getQuoteItems(quoteId);
      case 'POST':
        return this.createQuoteItem(quoteId, req.body);
      case 'PUT':
        if (itemId) return this.updateQuoteItem(quoteId, itemId, req.body);
        throw new Error('Item ID is required for update');
      case 'DELETE':
        if (itemId) return this.deleteQuoteItem(quoteId, itemId);
        throw new Error('Item ID is required for delete');
      default:
        throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  private getQuoteItems(quoteId: number): Observable<HttpEvent<any>> {
    const items = this.quoteItems.get(quoteId) || [];
    const res: ApiResponse<JobItem> = {
      data: items,
      total: items.length,
      page: 1,
      limit: items.length,
      totalPages: 1,
    };
    return this.mapToResponse(res, 200);
  }

  private createQuoteItem(quoteId: number, body: any): Observable<HttpEvent<any>> {
    const newItem: JobItem = {
      id: this.nextItemId++,
      ...body,
    };

    if (!this.quoteItems.has(quoteId)) {
      this.quoteItems.set(quoteId, []);
    }
    this.quoteItems.get(quoteId)!.push(newItem);
    this.saveItemsToStorage();
    return this.mapToResponse(newItem, 201);
  }

  private updateQuoteItem(quoteId: number, itemId: number, body: any): Observable<HttpEvent<any>> {
    const items = this.quoteItems.get(quoteId) || [];
    const index = items.findIndex((i) => i.id === itemId);
    if (index === -1) {
      return this.mapToResponse({ error: 'Item not found' }, 404);
    }

    items[index] = { ...items[index], ...body, id: itemId };
    this.saveItemsToStorage();
    return this.mapToResponse(items[index], 200);
  }

  private deleteQuoteItem(quoteId: number, itemId: number): Observable<HttpEvent<any>> {
    const items = this.quoteItems.get(quoteId) || [];
    const index = items.findIndex((i) => i.id === itemId);
    if (index === -1) {
      return this.mapToResponse({ error: 'Item not found' }, 404);
    }

    items.splice(index, 1);
    this.saveItemsToStorage();
    return this.mapToResponse(null, 200);
  }

  private loadItemsFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.ITEMS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.quoteItems = new Map(
          Object.entries(parsed.items || {}).map(([k, v]) => [Number(k), v as JobItem[]])
        );
        this.nextItemId = parsed.nextItemId || 1;
      }
    } catch (error) {
      console.error('Error loading quote items from localStorage:', error);
      this.quoteItems = new Map();
    }
  }

  private saveItemsToStorage(): void {
    try {
      const itemsObj: Record<number, JobItem[]> = {};
      this.quoteItems.forEach((items, key) => {
        itemsObj[key] = items;
      });
      localStorage.setItem(
        this.ITEMS_STORAGE_KEY,
        JSON.stringify({ items: itemsObj, nextItemId: this.nextItemId })
      );
    } catch (error) {
      console.error('Error saving quote items to localStorage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.quotes = parsed.quotes || [];
        this.nextId = parsed.nextId || 1;
      }
    } catch (error) {
      console.error('Error loading quotes from localStorage:', error);
      this.quotes = [];
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify({ quotes: this.quotes, nextId: this.nextId })
      );
    } catch (error) {
      console.error('Error saving quotes to localStorage:', error);
    }
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(delay(500));
  }

  private generateMockQuotes(): void {
    const customers = this.customerMocksService.getMockCustomers();
    const vehicles = this.vehicleMocksService.getMockVehicles();
    const statuses: QuoteStatus[] = [
      'PENDING',
      'ACCEPTED',
      'DECLINED',
      'EXPIRED',
      'IN_PROGRESS',
      'JOB_CREATED',
    ];

    for (let i = 1; i <= 50; i++) {
      const customer = faker.helpers.arrayElement(customers);
      const vehicle = faker.helpers.arrayElement(vehicles);
      const createdAt = faker.date.past();

      this.quotes.push({
        id: i,
        tenantId: 1,
        vehicleId: vehicle.id!,
        quoteNumber: `QT-01-${i}`,
        customerId: customer.id,
        customerName: customer.name,
        customerEmail: 'k@y.com',
        vehicleRego: vehicle.rego,
        jobId: null,
        status: faker.helpers.arrayElement(statuses),
        expiryDate: faker.date.soon({ days: 14, refDate: createdAt }).toISOString(),
        notes: faker.helpers.maybe(() => faker.lorem.sentence(), { probability: 0.6 }) || null,
        createdBy: null,
        updatedBy: null,
        createdAt: createdAt.toISOString(),
        updatedAt: createdAt.toISOString(),
      });
    }
    this.nextId = 51;
  }
}
